import { useState } from "react";
import { Page } from "../components/ui.jsx";
import { TRIBE } from "../data/tribe.js";

// The myTRiBE+ journey. Progress is deliberately local and ephemeral — the
// TRiBE+ leader keeps the official record, so this is a self-paced tracker only.
export default function Tribe({ back, onChat }) {
  const [done, setDone] = useState([]);
  const path = TRIBE.path;
  const isDone = (id) => done.includes(id);

  // You can only complete the next unfinished step, so the path stays a path.
  const frontier = (() => {
    let i = 0;
    for (const step of path) {
      if (step.type === "phase" || step.type === "finale") {
        i++;
        continue;
      }
      if (isDone(step.id)) {
        i++;
        continue;
      }
      break;
    }
    return i;
  })();

  const toggle = (step, index) => {
    if (step.type === "phase" || step.type === "finale") return;
    if (isDone(step.id)) {
      setDone(done.filter((id) => id !== step.id));
      return;
    }
    if (index <= frontier) setDone([...done, step.id]);
  };

  const courses = path.filter((s) => s.type === "course");
  const totalLessons = courses.reduce((sum, c) => sum + c.lessons, 0);
  const doneLessons = courses
    .filter((c) => isDone(c.id))
    .reduce((sum, c) => sum + c.lessons, 0);
  const pct = Math.round((doneLessons / totalLessons) * 100);

  // Phase state is derived from the steps that sit under each phase pin.
  const phaseState = TRIBE.phases.map((phase) => {
    const steps = [];
    let current = null;
    for (const step of path) {
      if (step.type === "phase") current = step.name;
      else if (current === phase && (step.type === "course" || step.type === "checkpoint")) {
        steps.push(step);
      }
    }
    const all = steps.every((s) => isDone(s.id));
    const some = steps.some((s) => isDone(s.id));
    return all ? "done" : some ? "active" : "todo";
  });

  const activePhase = phaseState.findIndex((s) => s !== "done");
  const allDone = phaseState.every((s) => s === "done");

  let sideCounter = 0;

  return (
    <Page back={back} eyebrow={TRIBE.sub} title={TRIBE.name} intro={TRIBE.intro} keepCase>
      <div className="journey-stats">
        <div className="ptext">
          <span>SERVE · PRAY · GIVE</span>
          <span>
            {doneLessons} of {totalLessons} lessons · {pct}%
          </span>
        </div>
        <div className="pbar">
          <div style={{ width: pct + "%" }} />
        </div>
        <div className="phase-dots">
          {TRIBE.phases.map((phase, i) => (
            <div
              key={phase}
              className={
                "phase-dot " +
                (phaseState[i] === "done" ? "done" : i === activePhase ? "active" : "")
              }
            >
              {phase}
            </div>
          ))}
        </div>
        <div className="pnote">
          Tap an unlocked class to mark it complete (tap again to undo). Progress resets
          when you close the hub — your TRiBE+ leader keeps the official record.
        </div>
      </div>

      {allDone && (
        <div className="disruptor">
          <h3>You're a Disruptor.</h3>
          <p>
            Every phase complete. Engage the culture. Live out loud. Your TRiBE+ leader
            will confirm your commissioning — there is more.
          </p>
        </div>
      )}

      <div className="path">
        {path.map((step, i) => {
          if (step.type === "phase") {
            const open =
              i === 0 ||
              path.slice(0, i).every((s) => s.type === "phase" || isDone(s.id));
            return (
              <div key={step.id} className={"phase-pin" + (open ? " open" : "")}>
                <div className="pt">{step.name}</div>
                <div className="ps">
                  {open
                    ? step.note === "You belong here"
                      ? step.note
                      : "You're in. Keep going."
                    : step.note}
                </div>
              </div>
            );
          }

          if (step.type === "finale") {
            const open = path
              .slice(0, i)
              .every((s) => s.type === "phase" || isDone(s.id));
            return (
              <div
                key={step.id}
                className="phase-pin finale-pin"
                style={{ opacity: open ? 1 : 0.55 }}
              >
                <div className="pt">{step.name}</div>
                <div className="ps">
                  {step.note}.{" "}
                  {open
                    ? "Talk to your leader about joining a TRiBE+ this season."
                    : "Finish the path to unlock."}
                </div>
              </div>
            );
          }

          const state = isDone(step.id) ? "done" : i <= frontier ? "avail" : "locked";
          const isCheckpoint = step.type === "checkpoint";
          const glyph = state === "done" ? "✓" : isCheckpoint ? "★" : "▶";
          const side = sideCounter++ % 2 === 0 ? "left" : "right";

          return (
            <div key={step.id} className={"prow " + side}>
              <button
                className={
                  "pnode" +
                  (isCheckpoint ? " cp" : "") +
                  (state === "avail" ? " avail" : "") +
                  (state === "done" ? " done" : "")
                }
                onClick={() => toggle(step, i)}
                aria-label={step.name + (state === "locked" ? " (locked)" : "")}
                disabled={state === "locked"}
              >
                {glyph}
              </button>
              <div className="plabel">
                <div className="pt">{step.name}</div>
                <div className="ps">
                  {isCheckpoint
                    ? "Quiz"
                    : `${isDone(step.id) ? step.lessons : 0}/${step.lessons} lessons`}
                  {!isCheckpoint && step.desc ? ` — ${step.desc}` : ""}
                </div>
                <button className="chatlink" onClick={() => onChat(step)}>
                  💬 Class chatter
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="block" style={{ marginTop: 28 }}>
        <h3>🧭 There Is More</h3>
        <p>
          Optional trails that branch off the main path whenever you want to go deeper.
          Watch the series, pass its checkpoint, earn the badge.
        </p>
      </div>

      {TRIBE.electives.map((e) => (
        <div key={e.name} className="card" style={e.soon ? { opacity: 0.6 } : undefined}>
          <div className="name">
            <span>{e.name}</span>
            {e.soon ? (
              <span className="badge">Soon</span>
            ) : (
              <span className="lockbadge">{e.lessons} lessons</span>
            )}
          </div>
          <div className="desc">{e.desc}</div>
        </div>
      ))}
    </Page>
  );
}
